/**
 * Determines the read receipt status of a message
 * @param {Object} message - The Sendbird message object
 * @param {string} currentUserId - The current user's ID
 * @param {Object} channel - The channel object
 * @returns {string} - 'sent', 'delivered', or 'read'
 */
export const getMessageStatus = (message, currentUserId, channel) => {
    if (!message || !currentUserId || !channel) {
        return 'sent';
    }

    // Only show read receipts for messages sent by the current user
    if (message.sender?.userId !== currentUserId) {
        return null;
    }

    // Check if message has been read by all members
    if (message.readStatus && channel.members) {
        const totalMembers = channel.members.length;
        const readMembers = Object.values(message.readStatus).filter(
            (readInfo) => readInfo.timestamp > 0,
        ).length;

        // If all members have read the message, it's read
        if (readMembers >= totalMembers - 1) { // -1 because sender doesn't count
            return 'read';
        }
    }

    // Check if message has been delivered (has sentAt timestamp)
    if (message.sentAt && message.sentAt > 0) {
        return 'delivered';
    }

    // Default to sent status
    return 'sent';
};

/**
 * Gets the read receipt status for display
 * @param {Object} message - The Sendbird message object
 * @param {string} currentUserId - The current user's ID
 * @param {Object} channel - The channel object
 * @returns {string|null} - 'sent', 'read', or null if not applicable
 */
export const getReadReceiptStatus = (message, currentUserId, channel) => {
    if (!message || !currentUserId) {
        return null;
    }

    // Only show read receipts for messages sent by the current user
    if (message.sender?.userId !== currentUserId) {
        return null;
    }

    // If no channel, default to sent
    if (!channel) {
        return 'sent';
    }

    // Check if message has been read by all members
    if (message.readStatus && channel.members) {
        const totalMembers = channel.members.length;
        const readMembers = Object.values(message.readStatus).filter(
            (readInfo) => readInfo.timestamp > 0,
        ).length;

        // If all members have read the message, it's read
        if (readMembers >= totalMembers - 1) { // -1 because sender doesn't count
            return 'read';
        }
    }

    // Default to sent status
    return 'sent';
};
